import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tag, Copy, Calendar, Sparkles, ArrowRight, Loader2, Wine, Mic2, Ghost, Gift, PartyPopper, Coffee } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SEASONS = [
  { label: 'Brunch', href: '/brunch', icon: Coffee, blurb: 'Bottomless mimosas & morning parties', gradient: 'from-amber-500/20 to-orange-500/10' },
  { label: 'Comedy', href: '/comedy', icon: Mic2, blurb: 'Stand-up shows & open mics', gradient: 'from-fuchsia-500/20 to-pink-500/10' },
  { label: 'Wine', href: '/wine', icon: Wine, blurb: 'Tastings, pairings & winery nights', gradient: 'from-rose-500/20 to-red-500/10' },
  { label: 'Halloween', href: '/halloween', icon: Ghost, blurb: 'Costume parties & haunted happenings', gradient: 'from-orange-500/20 to-purple-500/10' },
  { label: 'Holiday', href: '/holiday', icon: Gift, blurb: 'Festive markets & holiday parties', gradient: 'from-emerald-500/20 to-teal-500/10' },
  { label: 'NYE', href: '/nye', icon: PartyPopper, blurb: 'Ring in the new year in style', gradient: 'from-indigo-500/20 to-fuchsia-500/10' },
];

function discountLabel(d) {
  if (d.type === 'percent') return `${d.discount_amount}% off`;
  return `$${d.discount_amount} off`;
}

export default function DealsPage() {
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/public/deals`)
      .then(res => setDeals(res.data.deals || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const copyCode = (code) => {
    navigator.clipboard.writeText(code);
    toast.success(`Code ${code} copied — apply it at checkout`);
  };

  return (
    <div className="min-h-screen bg-background pt-20 pb-16" data-testid="deals-page">
      <Helmet>
        <title>Deals & Offers | Eventism</title>
        <meta name="description" content="Current promo codes, seasonal offers and event deals on Eventism. Save on tickets, experiences and reservations." />
        <link rel="canonical" href="https://eventism.com/deals" />
      </Helmet>

      <section className="max-w-6xl mx-auto px-4 pt-6 pb-10">
        <Badge className="mb-4 bg-fuchsia-500/15 text-fuchsia-500 border border-fuchsia-500/30 hover:bg-fuchsia-500/15">
          <Tag className="h-3 w-3 mr-1" /> Live offers
        </Badge>
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight">
          Deals &amp; <span className="bg-gradient-to-r from-fuchsia-500 via-indigo-500 to-amber-500 bg-clip-text text-transparent">Offers</span>
        </h1>
        <p className="mt-3 text-base text-muted-foreground max-w-2xl">
          Promo codes and seasonal savings on tickets, experiences and reservations. Copy a code and apply it at checkout.
        </p>
      </section>

      <section className="max-w-6xl mx-auto px-4 pb-14">
        {loading ? (
          <div className="flex items-center justify-center py-16 text-muted-foreground">
            <Loader2 className="h-6 w-6 animate-spin" />
          </div>
        ) : deals.length === 0 ? (
          <Card className="border-dashed" data-testid="deals-empty-state">
            <CardContent className="py-14 text-center">
              <Sparkles className="h-8 w-8 mx-auto text-muted-foreground mb-3" />
              <p className="font-medium">No public deals live right now</p>
              <p className="text-sm text-muted-foreground mt-1">Check back soon — or browse what's on this week.</p>
              <Link to="/find-events">
                <Button className="mt-5 rounded-full bg-gradient-to-r from-fuchsia-500 to-indigo-500 text-white hover:opacity-90" data-testid="deals-empty-cta">
                  Find Events <ArrowRight className="ml-1 h-4 w-4" />
                </Button>
              </Link>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="deals-grid">
            {deals.map((d) => (
              <Card key={d.code} className="border-border overflow-hidden group hover:border-fuchsia-500/50 transition-colors" data-testid={`deal-card-${d.code}`}>
                <CardContent className="p-5">
                  <div className="flex items-center justify-between mb-3">
                    <Badge className="bg-emerald-500/15 text-emerald-500 border border-emerald-500/30 hover:bg-emerald-500/15">
                      {discountLabel(d)}
                    </Badge>
                    {d.expires_at && (
                      <span className="text-[11px] text-muted-foreground flex items-center gap-1">
                        <Calendar className="h-3 w-3" /> ends {String(d.expires_at).slice(0, 10)}
                      </span>
                    )}
                  </div>
                  <p className="text-sm font-medium min-h-[2.5rem]">{d.description || 'Eventism promo'}</p>
                  {d.min_order > 0 && (
                    <p className="text-xs text-muted-foreground mt-1">Min. order ${d.min_order}</p>
                  )}
                  <button
                    onClick={() => copyCode(d.code)}
                    data-testid={`deal-copy-${d.code}`}
                    className="mt-4 w-full flex items-center justify-between rounded-lg border border-dashed border-fuchsia-500/50 bg-fuchsia-500/5 px-4 py-2.5 font-mono text-sm font-semibold tracking-wider hover:bg-fuchsia-500/10 transition-colors"
                  >
                    {d.code}
                    <Copy className="h-4 w-4 text-fuchsia-500" />
                  </button>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </section>

      <section className="max-w-6xl mx-auto px-4">
        <h2 className="text-lg font-semibold mb-1">Seasonal savings</h2>
        <p className="text-sm text-muted-foreground mb-5">The best times of year to grab tickets early.</p>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          {SEASONS.map((s) => (
            <Link key={s.label} to={s.href} data-testid={`deals-season-${s.label.toLowerCase()}`}
              className={`group rounded-xl border border-border bg-gradient-to-br ${s.gradient} p-5 hover:border-fuchsia-500/40 transition-colors`}>
              <s.icon className="h-5 w-5 mb-3 text-foreground/80 group-hover:translate-x-1 transition-transform" />
              <div className="font-semibold text-sm">{s.label}</div>
              <p className="text-xs text-muted-foreground mt-1 hidden sm:block">{s.blurb}</p>
            </Link>
          ))}
        </div>
      </section>
    </div>
  );
}
